import { Contract, Interface, type BrowserProvider } from 'ethers'
import { ERC20_ABI } from '../constants/abis'

export const MULTICALL3_ADDRESS = '0xcA11bde05977b3631167028862bE2a173976CA11'

const AGGREGATE3_ABI = [
  'function aggregate3((address target, bool allowFailure, bytes callData)[] calls) payable returns ((bool success, bytes returnData)[] returnData)',
] as const

const erc20Iface = new Interface(ERC20_ABI)

export interface BatchBalanceAllowanceResult {
  balIn: bigint
  balOut: bigint
  allowanceIn: bigint
}

export async function batchBalanceAndAllowance(
  provider: BrowserProvider,
  owner: string,
  spender: string,
  tokenIn: string | null,
  tokenOut: string | null,
): Promise<BatchBalanceAllowanceResult> {
  const fetchSeparate = async (): Promise<BatchBalanceAllowanceResult> => {
    const bal = async (addr: string | null) => {
      if (!addr) return 0n
      const c = new Contract(addr, ERC20_ABI, provider)
      return (await c.balanceOf(owner)) as bigint
    }
    const allow = async () => {
      if (!tokenIn) return 0n
      const c = new Contract(tokenIn, ERC20_ABI, provider)
      return (await c.allowance(owner, spender)) as bigint
    }
    const [balIn, balOut, allowanceIn] = await Promise.all([
      bal(tokenIn),
      bal(tokenOut),
      allow(),
    ])
    return { balIn, balOut, allowanceIn }
  }

  if (!tokenIn && !tokenOut) {
    return { balIn: 0n, balOut: 0n, allowanceIn: 0n }
  }

  const calls: { target: string; allowFailure: boolean; callData: string }[] = []
  if (tokenIn) {
    calls.push({
      target: tokenIn,
      allowFailure: true,
      callData: erc20Iface.encodeFunctionData('balanceOf', [owner]),
    })
    calls.push({
      target: tokenIn,
      allowFailure: true,
      callData: erc20Iface.encodeFunctionData('allowance', [owner, spender]),
    })
  }
  if (tokenOut) {
    calls.push({
      target: tokenOut,
      allowFailure: true,
      callData: erc20Iface.encodeFunctionData('balanceOf', [owner]),
    })
  }

  try {
    const mc = new Contract(MULTICALL3_ADDRESS, AGGREGATE3_ABI, provider)
    const raw = (await mc.aggregate3(calls)) as { success: boolean; returnData: string }[]

    let bi = 0n
    let bo = 0n
    let al = 0n
    let i = 0
    if (tokenIn) {
      const r0 = raw[i++]
      if (r0?.success && r0.returnData !== '0x')
        bi = erc20Iface.decodeFunctionResult('balanceOf', r0.returnData)[0] as bigint
      const r1 = raw[i++]
      if (r1?.success && r1.returnData !== '0x')
        al = erc20Iface.decodeFunctionResult('allowance', r1.returnData)[0] as bigint
    }
    if (tokenOut) {
      const r2 = raw[i++]
      if (r2?.success && r2.returnData !== '0x')
        bo = erc20Iface.decodeFunctionResult('balanceOf', r2.returnData)[0] as bigint
    }
    return { balIn: bi, balOut: bo, allowanceIn: al }
  } catch {
    return fetchSeparate()
  }
}

/** Batch `balanceOf` for many ERC-20s via Multicall3; falls back to sequential RPC if aggregate fails. */
export async function batchBalancesOf(
  provider: BrowserProvider,
  owner: string,
  tokenAddresses: string[],
): Promise<bigint[]> {
  if (tokenAddresses.length === 0) return []

  const decodeOne = (returnData: string): bigint => {
    if (!returnData || returnData === '0x') return 0n
    try {
      return erc20Iface.decodeFunctionResult('balanceOf', returnData)[0] as bigint
    } catch {
      return 0n
    }
  }

  const calls = tokenAddresses.map((target) => ({
    target,
    allowFailure: true,
    callData: erc20Iface.encodeFunctionData('balanceOf', [owner]),
  }))

  try {
    const mc = new Contract(MULTICALL3_ADDRESS, AGGREGATE3_ABI, provider)
    const raw = (await mc.aggregate3(calls)) as { success: boolean; returnData: string }[]
    return raw.map((r) => (r?.success ? decodeOne(r.returnData) : 0n))
  } catch {
    const out: bigint[] = []
    for (const addr of tokenAddresses) {
      try {
        const c = new Contract(addr, ERC20_ABI, provider)
        out.push((await c.balanceOf(owner)) as bigint)
      } catch {
        out.push(0n)
      }
    }
    return out
  }
}
